/**
 * MatchResults.tsx — Dashboard.
 *
 * Rebuilt in Pass 2 using the component library.
 * Preserves all data sources and all three modes (admin / client / demo).
 *
 * Before: 634 lines with inline DonutChart, LineChart, BarChart, MetricCard.
 * After:  ~430 lines, charts extracted to local helpers, KPIs use StatCard,
 *         rainbow card palette replaced with semantic colors + glow on primary.
 */

import { useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import { useBreakpoint } from '../../hooks/useMediaQuery';
import {
  PageHeader, LiveDot,
  StatCard, StatGrid,
  Card, CardTabs,
  EmptyState, Skeleton,
  Icon,
  Tag,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface Stats {
  // Admin mode
  client_records?: number;
  active_clients?: number;
  registry_outlets?: number;
  matched_outlets?: number;
  matched_records?: number;
  unmatched_records?: number;
  pending_records?: number;
  // Client mode
  your_outlets?: number;
  match_rate?: number;
  total_submitted?: number;
  // Demo mode
  demo_clients?: number;
  total_records?: number;
  matched?: number;
  // Client + demo (added by FIX 5 — returned directly by backend so the donut
  // segments don't overlap: matched + pending_review + unmatched = total).
  unmatched?: number;
  // Common
  pending_review?: number;
}

const MATCH_RATE_TOOLTIP =
  'Match rate shows confirmed outlet matches as a percentage of total submitted records. ' +
  'Records in review have a candidate match awaiting confirmation. ' +
  'Unmatched records had no viable candidate found.';

interface ChartDataPoint {
  date?: string;
  [key: string]: string | number | undefined;
}

/* ═══════════════ CHART PRIMITIVES ═══════════════ */
/* These are local — they can move to components/ui later if reused. */

function AreaLineChart({
  data,
  lines,
  height = 220,
}: {
  data: ChartDataPoint[];
  lines: { key: string; color: string; label: string; dashed?: boolean }[];
  height?: number;
}) {
  const width = 800;
  const padding = { top: 16, right: 16, bottom: 32, left: 56 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  if (data.length < 2) {
    return (
      <div style={{ height }}>
        <EmptyState
          compact
          icon={<Icon.Activity size={18} />}
          title="Trend data accumulates daily"
          description="Check back tomorrow — your first data points will appear here."
        />
      </div>
    );
  }

  const allValues = lines.flatMap(l => data.map(d => Number(d[l.key]) || 0));
  const maxVal = Math.max(...allValues);
  const minVal = Math.min(...allValues, 0);
  const range = maxVal - minVal || 1;

  const xScale = (i: number) => padding.left + (i / (data.length - 1)) * chartW;
  const yScale = (v: number) => padding.top + chartH - ((v - minVal) / range) * chartH;

  const primaryLine = lines.find(l => !l.dashed) ?? lines[0];
  const areaPoints = data.map((d, i) =>
    `${xScale(i)},${yScale(Number(d[primaryLine.key]) || 0)}`
  ).join(' ');
  const areaPath = `M${xScale(0)},${yScale(minVal)} L${areaPoints
    .split(' ')
    .join(' L')} L${xScale(data.length - 1)},${yScale(minVal)} Z`;

  return (
    <div style={{ height }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
        <defs>
          <linearGradient id="area-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={primaryLine.color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={primaryLine.color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Gridlines + labels */}
        {[0, 0.25, 0.5, 0.75, 1].map(t => {
          const y = padding.top + t * chartH;
          const val = Math.round(maxVal - t * range);
          return (
            <g key={t}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y}
                stroke="rgba(148,163,184,0.06)" strokeWidth={1} />
              <text x={padding.left - 8} y={y + 4} textAnchor="end"
                fontSize={10} fill={theme.text.quaternary} fontFamily={theme.font.data}>
                {val.toLocaleString()}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        <path d={areaPath} fill="url(#area-grad)" />

        {/* Lines */}
        {lines.map(line => {
          const points = data.map((d, i) =>
            `${xScale(i)},${yScale(Number(d[line.key]) || 0)}`
          ).join(' ');
          return (
            <polyline
              key={line.key}
              points={points}
              fill="none"
              stroke={line.color}
              strokeWidth={line.dashed ? 1.5 : 2}
              strokeDasharray={line.dashed ? '4 4' : undefined}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={line.dashed ? 0.6 : 1}
              style={!line.dashed ? { filter: `drop-shadow(0 0 3px ${theme.brand.cyanGlow})` } : undefined}
            />
          );
        })}

        {/* End dot on primary line */}
        {data.length > 0 && (
          <>
            <circle
              cx={xScale(data.length - 1)}
              cy={yScale(Number(data[data.length - 1][primaryLine.key]) || 0)}
              r={4}
              fill={primaryLine.color}
              style={{ filter: `drop-shadow(0 0 6px ${theme.brand.cyanGlow})` }}
            />
            <circle
              cx={xScale(data.length - 1)}
              cy={yScale(Number(data[data.length - 1][primaryLine.key]) || 0)}
              r={8}
              fill={primaryLine.color}
              opacity={0.15}
            />
          </>
        )}

        {/* X labels */}
        {[0, Math.floor(data.length / 2), data.length - 1].map(i => (
          <text
            key={i}
            x={xScale(i)}
            y={height - 8}
            textAnchor="middle"
            fontSize={10}
            fill={theme.text.quaternary}
            fontFamily={theme.font.data}
          >
            {data[i]?.date?.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

function BarChart({
  data,
  valueKey,
  height = 220,
}: {
  data: ChartDataPoint[];
  valueKey: string;
  height?: number;
}) {
  const width = 800;
  const padding = { top: 16, right: 16, bottom: 32, left: 56 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  if (!data.length) {
    return (
      <div style={{ height }}>
        <EmptyState
          compact
          icon={<Icon.Activity size={18} />}
          title="No pipeline activity"
          description="No submissions or reviews in the last 30 days."
        />
      </div>
    );
  }

  const maxVal = Math.max(...data.map(d => Number(d[valueKey]) || 0), 1);
  const gap = chartW / data.length;
  const barWidth = Math.max(4, gap * 0.7);

  return (
    <div style={{ height }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
        {[0, 0.5, 1].map(t => {
          const y = padding.top + t * chartH;
          return (
            <g key={t}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y}
                stroke="rgba(148,163,184,0.06)" strokeWidth={1} />
              <text x={padding.left - 8} y={y + 4} textAnchor="end"
                fontSize={10} fill={theme.text.quaternary} fontFamily={theme.font.data}>
                {Math.round(maxVal * (1 - t)).toLocaleString()}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const val = Number(d[valueKey]) || 0;
          const barH = (val / maxVal) * chartH;
          const x = padding.left + i * gap + (gap - barWidth) / 2;
          const y = padding.top + chartH - barH;
          return (
            <rect
              key={i}
              x={x} y={y}
              width={barWidth} height={barH}
              fill={theme.brand.cyan}
              opacity={0.75}
              rx={2}
            />
          );
        })}
        {[0, Math.floor(data.length / 2), data.length - 1].map(i => (
          <text key={i} x={padding.left + i * gap + gap / 2} y={height - 8}
            textAnchor="middle" fontSize={10}
            fill={theme.text.quaternary} fontFamily={theme.font.data}>
            {data[i]?.date?.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

/* ═══════════════ MATCH RATE HERO ═══════════════ */
/* Primary KPI visualization: big ring + breakdown. Used in admin mode. */

function MatchRateHero({
  matched, pending, unmatched, total, label = 'Match rate',
}: {
  matched: number;
  pending: number;
  unmatched: number;
  total: number;
  label?: string;
}) {
  const pct = total > 0 ? (matched / total) * 100 : 0;
  const circumference = 2 * Math.PI * 66;

  const matchedPct = total > 0 ? (matched / total * 100).toFixed(1) : '0.0';
  const pendingPct = total > 0 ? (pending / total * 100).toFixed(1) : '0.0';
  const unmatchedPct = total > 0 ? (unmatched / total * 100).toFixed(1) : '0.0';

  // Arc lengths along the ring. Segments with 0 length render nothing — the
  // conditional skip keeps the SVG clean for all-matched clients.
  const matchedLen   = total > 0 ? (matched   / total) * circumference : 0;
  const pendingLen   = total > 0 ? (pending   / total) * circumference : 0;
  const unmatchedLen = total > 0 ? (unmatched / total) * circumference : 0;

  // Active segment count tells us whether to use rounded caps (single segment,
  // looks like a progress arc) or butt caps (multi-segment, crisp boundaries).
  const segmentCount = [matchedLen, pendingLen, unmatchedLen].filter(l => l > 0).length;
  const singleSegment = segmentCount <= 1;
  const capStyle: 'butt' | 'round' = singleSegment ? 'round' : 'butt';

  return (
    <Card variant="primary" padding={6} style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', left: -100, top: -100,
        width: 300, height: 300,
        background: `radial-gradient(circle, ${theme.brand.cyanSoft} 0%, transparent 60%)`,
        pointerEvents: 'none',
      }} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: theme.space[8],
        alignItems: 'center',
        position: 'relative',
      }}>
        {/* Ring */}
        <div
          title={MATCH_RATE_TOOLTIP}
          style={{ position: 'relative', width: 160, height: 160, flexShrink: 0 }}>
          <svg width="160" height="160" viewBox="0 0 160 160" style={{
            transform: 'rotate(-90deg)',
            filter: `drop-shadow(0 0 12px ${theme.brand.cyanGlow})`,
          }}>
            <defs>
              <linearGradient id="ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={theme.brand.cyan} />
                <stop offset="100%" stopColor={theme.brand.cyanLight} />
              </linearGradient>
            </defs>
            {/* Track */}
            <circle cx="80" cy="80" r="66" fill="none"
              stroke="rgba(148,163,184,0.08)" strokeWidth="10" />
            {/* Matched (cyan) — starts at 12 o'clock */}
            {matchedLen > 0 && (
              <circle cx="80" cy="80" r="66" fill="none"
                stroke="url(#ring-grad)" strokeWidth="10"
                strokeLinecap={capStyle}
                strokeDasharray={`${matchedLen} ${circumference}`}
                strokeDashoffset={0} />
            )}
            {/* Pending review (amber) — begins where matched ends */}
            {pendingLen > 0 && (
              <circle cx="80" cy="80" r="66" fill="none"
                stroke={theme.semantic.warning} strokeWidth="10"
                strokeLinecap={capStyle}
                strokeDasharray={`${pendingLen} ${circumference}`}
                strokeDashoffset={-matchedLen} />
            )}
            {/* Unmatched (danger) — fills the remainder */}
            {unmatchedLen > 0 && (
              <circle cx="80" cy="80" r="66" fill="none"
                stroke={theme.semantic.danger} strokeWidth="10"
                strokeLinecap={capStyle}
                strokeDasharray={`${unmatchedLen} ${circumference}`}
                strokeDashoffset={-(matchedLen + pendingLen)} />
            )}
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              fontSize: 36,
              fontWeight: theme.fontWeight.medium,
              letterSpacing: theme.letterSpacing.tight,
              color: theme.text.primary,
              fontFamily: theme.font.data,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}>
              {pct.toFixed(1)}<span style={{ fontSize: 20, color: theme.text.tertiary }}>%</span>
            </div>
            <div style={{
              fontSize: 10,
              fontWeight: theme.fontWeight.medium,
              letterSpacing: theme.letterSpacing.wider,
              textTransform: 'uppercase',
              color: theme.text.tertiary,
              marginTop: 6,
              whiteSpace: 'nowrap',
            }}>{label}</div>
          </div>
        </div>

        {/* Breakdown */}
        <div>
          <div style={{
            fontSize: theme.fontSize.md,
            fontWeight: theme.fontWeight.semibold,
            color: theme.text.primary,
            marginBottom: theme.space[3],
          }}>Match quality breakdown</div>

          <BreakdownRow
            swatchColor={theme.brand.cyan} glow
            label="Successfully matched"
            value={matched.toLocaleString()}
            pct={`${matchedPct}%`}
          />
          <BreakdownRow
            swatchColor={theme.semantic.warning}
            label="Pending review"
            value={pending.toLocaleString()}
            pct={`${pendingPct}%`}
          />
          <BreakdownRow
            swatchColor={theme.semantic.danger}
            label="Unmatched"
            value={unmatched.toLocaleString()}
            pct={`${unmatchedPct}%`}
            last
          />
        </div>
      </div>
    </Card>
  );
}

function BreakdownRow({
  swatchColor, glow, label, value, pct, last,
}: {
  swatchColor: string;
  glow?: boolean;
  label: ReactNode;
  value: ReactNode;
  pct: ReactNode;
  last?: boolean;
}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '12px 1fr auto auto',
      gap: theme.space[3],
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: last ? 'none' : `1px solid ${theme.borderColor.subtle}`,
    }}>
      <span style={{
        width: 8, height: 8,
        borderRadius: 2,
        background: swatchColor,
        boxShadow: glow ? `0 0 6px ${theme.brand.cyanGlow}` : 'none',
      }} />
      <span style={{ fontSize: theme.fontSize.base, color: theme.text.secondary }}>{label}</span>
      <span style={{
        fontFamily: theme.font.data,
        fontSize: theme.fontSize.base,
        color: theme.text.primary,
        fontWeight: theme.fontWeight.medium,
        fontVariantNumeric: 'tabular-nums',
      }}>{value}</span>
      <span style={{
        fontFamily: theme.font.data,
        fontSize: theme.fontSize.xs,
        color: theme.text.tertiary,
        minWidth: 52,
        textAlign: 'right',
      }}>{pct}</span>
    </div>
  );
}

/* ═══════════════ COVERAGE RING (secondary) ═══════════════ */
/* Smaller ring for "Registry Coverage" metric. */

function CoverageRing({ pct, label, subtitle }: { pct: number; label: string; subtitle?: string }) {
  const circumference = 2 * Math.PI * 48;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: theme.space[5],
    }}>
      <div style={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
        <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="60" cy="60" r="48" fill="none" stroke="rgba(148,163,184,0.08)" strokeWidth="8" />
          <circle cx="60" cy="60" r="48" fill="none" stroke={theme.brand.cyan} strokeWidth="8"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} />
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            fontSize: 24,
            fontWeight: theme.fontWeight.semibold,
            color: theme.text.primary,
            fontFamily: theme.font.data,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}>
            {pct.toFixed(1)}<span style={{ fontSize: 14, color: theme.text.tertiary }}>%</span>
          </div>
        </div>
      </div>

      <div>
        <div style={{
          fontSize: theme.fontSize.md,
          fontWeight: theme.fontWeight.semibold,
          color: theme.text.primary,
          marginBottom: 4,
        }}>{label}</div>
        {subtitle && (
          <div style={{
            fontSize: theme.fontSize.sm,
            color: theme.text.tertiary,
            maxWidth: 240,
            lineHeight: 1.4,
          }}>{subtitle}</div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════ COMPONENT ═══════════════ */

export default function MatchResults() {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { adminMode, viewAsClient, modeVersion } = useAdmin();

  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<ChartDataPoint[]>([]);
  const [pipelineActivity, setPipelineActivity] = useState<ChartDataPoint[]>([]);
  const [chartRange, setChartRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    document.title = 'Dashboard | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api.get<Stats>('/api/stats', { signal: controller.signal })
      .then(res => setStats(res.data))
      .catch(err => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError('Failed to load dashboard data');
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [modeVersion]);

  useEffect(() => {
    if (!adminMode) {
      setMetricsHistory([]);
      setPipelineActivity([]);
      return;
    }
    const controller = new AbortController();
    const days = chartRange === '7d' ? 7 : chartRange === '90d' ? 90 : 30;
    Promise.all([
      api.get(`/api/metrics/history?days=${days}`, { signal: controller.signal }),
      api.get(`/api/metrics/pipeline-activity?days=${days}`, { signal: controller.signal }),
    ])
      .then(([h, p]) => {
        setMetricsHistory(h.data?.history ?? []);
        setPipelineActivity(p.data?.activity ?? []);
      })
      .catch(err => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      });
    return () => controller.abort();
  }, [adminMode, modeVersion, chartRange]);

  /* ══════ Loading state ══════ */
  if (loading && !stats) {
    return (
      <div style={{ padding: `${theme.space[8]} ${theme.space[10]}` }}>
        <Skeleton width="240px" height={32} style={{ marginBottom: 12 }} />
        <Skeleton width="320px" height={14} style={{ marginBottom: 32 }} />
        <Skeleton width="100%" height={120} style={{ marginBottom: 24 }} />
        <Skeleton width="100%" height={240} style={{ marginBottom: 24 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          <Skeleton width="100%" height={300} />
          <Skeleton width="100%" height={300} />
        </div>
      </div>
    );
  }

  /* ══════ Error state ══════ */
  if (error) {
    return (
      <div style={{ padding: `${theme.space[8]} ${theme.space[10]}` }}>
        <EmptyState
          icon={<Icon.AlertCircle size={18} />}
          title="Couldn't load dashboard"
          description={error}
        />
      </div>
    );
  }

  const isClientView = !!viewAsClient;
  const isDemoMode = !adminMode && !isClientView;

  /* ══════ Header ══════ */
  const header = (
    <PageHeader
      title="Dashboard"
      meta={
        <>
          <LiveDot>Live registry</LiveDot>
          <span style={{ color: theme.text.quaternary }}>·</span>
          <span>synced just now</span>
          {adminMode && (
            <>
              <span style={{ color: theme.text.quaternary }}>·</span>
              <Tag kind="purple" size="sm">Admin</Tag>
            </>
          )}
          {isClientView && (
            <>
              <span style={{ color: theme.text.quaternary }}>·</span>
              <Tag kind="cyan" size="sm">{viewAsClient}</Tag>
            </>
          )}
          {isDemoMode && (
            <>
              <span style={{ color: theme.text.quaternary }}>·</span>
              <Tag kind="warning" size="sm">Demo</Tag>
            </>
          )}
        </>
      }
    />
  );

  return (
    <div style={{
      padding: isMobile ? `${theme.space[6]} ${theme.space[4]}` : `${theme.space[8]} ${theme.space[10]}`,
      maxWidth: 1600,
      margin: '0 auto',
    }}>
      {header}

      {adminMode && <AdminDashboard
        stats={stats}
        metricsHistory={metricsHistory}
        pipelineActivity={pipelineActivity}
        chartRange={chartRange}
        setChartRange={setChartRange}
        navigate={navigate}
        isMobile={isMobile}
      />}

      {isClientView && <ClientDashboard stats={stats} navigate={navigate} isMobile={isMobile} />}

      {isDemoMode && <DemoDashboard stats={stats} navigate={navigate} isMobile={isMobile} />}
    </div>
  );
}

/* ═══════════════ ADMIN DASHBOARD ═══════════════ */

function AdminDashboard({
  stats, metricsHistory, pipelineActivity, chartRange, setChartRange, navigate, isMobile,
}: {
  stats: Stats | null;
  metricsHistory: ChartDataPoint[];
  pipelineActivity: ChartDataPoint[];
  chartRange: '7d' | '30d' | '90d';
  setChartRange: (r: '7d' | '30d' | '90d') => void;
  navigate: (path: string) => void;
  isMobile: boolean;
}) {
  const clientRecords = stats?.client_records ?? 0;
  const activeClients = stats?.active_clients ?? 0;
  const registryOutlets = stats?.registry_outlets ?? 0;
  const matchedOutlets = stats?.matched_outlets ?? 0;
  const pendingReview = stats?.pending_review ?? 0;
  const matchedRecords = stats?.matched_records ?? 0;
  const unmatchedRecords = stats?.unmatched_records ?? 0;
  const pendingRecords = stats?.pending_records ?? 0;
  const coveragePct = registryOutlets > 0 ? (matchedOutlets / registryOutlets) * 100 : 0;
  const matchRatePct = clientRecords > 0 ? (matchedRecords / clientRecords) * 100 : 0;

  return (
    <>
      {/* KPI strip — one primary (glow), rest neutral */}
      <StatGrid columns={isMobile ? 'repeat(2, 1fr)' : '1.4fr 1fr 1fr 1fr 1fr'}>
        <StatCard
          primary
          label="Match rate · records"
          value={matchRatePct.toFixed(1)}
          unit="%"
          delta={matchedRecords > 0 ? `${matchedRecords.toLocaleString()} of ${clientRecords.toLocaleString()}` : '—'}
          deltaType="neutral"
        />
        <ClickableStat onClick={() => navigate('/census-database')}>
          <StatCard
            label="Registry outlets"
            value={registryOutlets.toLocaleString()}
            delta={`${coveragePct.toFixed(1)}% covered`}
            deltaType="neutral"
          />
        </ClickableStat>
        <ClickableStat onClick={() => navigate('/matched-outlets')}>
          <StatCard
            label="Matched outlets"
            value={matchedOutlets.toLocaleString()}
          />
        </ClickableStat>
        <ClickableStat onClick={() => navigate('/clients')}>
          <StatCard
            label="Active clients"
            value={activeClients.toLocaleString()}
          />
        </ClickableStat>
        <ClickableStat onClick={() => navigate('/review-queue')}>
          <StatCard
            label="Pending review"
            value={pendingReview.toLocaleString()}
            deltaType={pendingReview > 0 ? 'down' : 'neutral'}
            delta={pendingReview > 0 ? 'Needs attention' : 'All clear'}
          />
        </ClickableStat>
      </StatGrid>

      <div style={{ height: theme.space[6] }} />

      {/* Match rate hero */}
      <MatchRateHero
        matched={matchedRecords}
        pending={pendingRecords}
        unmatched={unmatchedRecords}
        total={clientRecords}
      />

      <div style={{ height: theme.space[6] }} />

      {/* Registry growth + Coverage side by side */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
        gap: theme.space[6],
      }}>
        <Card
          title="Registry growth"
          subtitle={`New outlets added over the last ${chartRange === '7d' ? '7 days' : chartRange === '90d' ? '90 days' : '30 days'}`}
          action={
            <CardTabs
              tabs={[
                { key: '7d', label: '7d' },
                { key: '30d', label: '30d' },
                { key: '90d', label: '90d' },
              ]}
              active={chartRange}
              onChange={setChartRange}
            />
          }
        >
          <AreaLineChart
            data={metricsHistory}
            lines={[
              { key: 'matched_outlets', color: theme.brand.cyan, label: 'Matched outlets' },
              { key: 'registry_outlets', color: theme.text.secondary, label: 'Total registry', dashed: true },
            ]}
          />
          {metricsHistory.length >= 2 && (
            <div style={{
              display: 'flex',
              gap: theme.space[5],
              marginTop: theme.space[4],
              paddingTop: theme.space[4],
              borderTop: `1px solid ${theme.borderColor.subtle}`,
              flexWrap: 'wrap',
            }}>
              <LegendItem color={theme.brand.cyan} glow label="Matched outlets"
                value={matchedOutlets.toLocaleString()} />
              <LegendItem color={theme.text.secondary} dashed label="Total registry"
                value={registryOutlets.toLocaleString()} />
            </div>
          )}
        </Card>

        <Card title="Registry coverage" subtitle="Outlets mapped vs market total">
          <CoverageRing
            pct={coveragePct}
            label={`${matchedOutlets.toLocaleString()} of ${registryOutlets.toLocaleString()}`}
            subtitle="Physical outlets in the registry that have been claimed by at least one client."
          />
        </Card>
      </div>

      <div style={{ height: theme.space[6] }} />

      {/* Pipeline activity full-width */}
      <Card
        title="Pipeline activity"
        subtitle={`Submissions and matching jobs over the last ${chartRange === '7d' ? '7 days' : chartRange === '90d' ? '90 days' : '30 days'}`}
      >
        <BarChart data={pipelineActivity} valueKey="jobs_run" />
      </Card>
    </>
  );
}

/* ═══════════════ CLIENT DASHBOARD ═══════════════ */

function ClientDashboard({
  stats, navigate, isMobile,
}: {
  stats: Stats | null;
  navigate: (path: string) => void;
  isMobile: boolean;
}) {
  const { viewAsClient, userClientName } = useAdmin();
  const effectiveClient = viewAsClient || userClientName;
  const yourOutlets    = stats?.your_outlets ?? 0;
  const totalSubmitted = stats?.total_submitted ?? 0;
  const pendingReview  = stats?.pending_review ?? 0;
  // Prefer backend-computed unmatched (three clean buckets from FIX 5);
  // fall back to the subtraction in case an older /api/stats response arrives.
  const unmatched      = stats?.unmatched ?? Math.max(0, totalSubmitted - yourOutlets - pendingReview);
  const matchPct       = totalSubmitted > 0 ? (yourOutlets / totalSubmitted) * 100 : 0;

  return (
    <>
      {/*
       * 5-card KPI strip — match rate on the left (primary/glow), followed by
       * four neutral stats. Matches the landing-page mockup; each card fills
       * equal height thanks to the StatCard grid alignment fix.
       */}
      <StatGrid columns={isMobile ? 'repeat(2, 1fr)' : '1.4fr 1fr 1fr 1fr 1fr'}>
        <StatCard
          primary
          label="Match rate"
          value={matchPct.toFixed(1)}
          unit="%"
          delta={`${yourOutlets.toLocaleString()} of ${totalSubmitted.toLocaleString()}`}
          deltaType="neutral"
        />
        <ClickableStat onClick={() => navigate('/my-records')}>
          <StatCard
            label="My records"
            value={totalSubmitted.toLocaleString()}
          />
        </ClickableStat>
        <ClickableStat onClick={() => navigate('/matched-outlets')}>
          <StatCard
            label="Matched outlets"
            value={yourOutlets.toLocaleString()}
          />
        </ClickableStat>
        <StatCard
          label="Unmatched"
          value={unmatched.toLocaleString()}
          deltaType={unmatched > 0 ? 'down' : 'neutral'}
          delta={unmatched > 0 ? 'No Meridian match' : undefined}
        />
        <ClickableStat onClick={() => navigate('/review-queue')}>
          <StatCard
            label="Pending review"
            value={pendingReview.toLocaleString()}
            deltaType={pendingReview > 0 ? 'down' : 'neutral'}
            delta={pendingReview > 0 ? 'Needs attention' : 'All clear'}
          />
        </ClickableStat>
      </StatGrid>

      <div style={{ height: theme.space[6] }} />

      <MatchRateHero
        matched={yourOutlets}
        pending={pendingReview}
        unmatched={unmatched}
        total={totalSubmitted}
      />

      {unmatched > 0 && <UnmatchedSection clientName={effectiveClient} />}
    </>
  );
}

/* ═══════════════ DEMO DASHBOARD ═══════════════ */

function DemoDashboard({
  stats, navigate, isMobile,
}: {
  stats: Stats | null;
  navigate: (path: string) => void;
  isMobile: boolean;
}) {
  const demoClients = stats?.demo_clients ?? 0;
  const totalRecords = stats?.total_records ?? 0;
  const matched = stats?.matched ?? 0;
  const pendingReview = stats?.pending_review ?? 0;
  // Prefer backend-computed unmatched (FIX 5); fall back to subtraction.
  const unmatched = stats?.unmatched ?? Math.max(0, totalRecords - matched - pendingReview);
  const matchPct = totalRecords > 0 ? (matched / totalRecords) * 100 : 0;

  return (
    <>
      <StatGrid columns={isMobile ? 'repeat(2, 1fr)' : '1.4fr 1fr 1fr 1fr'}>
        <StatCard
          primary
          label="Demo match rate"
          value={matchPct.toFixed(1)}
          unit="%"
        />
        <StatCard
          label="Demo clients"
          value={demoClients.toLocaleString()}
        />
        <ClickableStat onClick={() => navigate('/my-records')}>
          <StatCard
            label="Total records"
            value={totalRecords.toLocaleString()}
          />
        </ClickableStat>
        <ClickableStat onClick={() => navigate('/review-queue')}>
          <StatCard
            label="Pending review"
            value={pendingReview.toLocaleString()}
          />
        </ClickableStat>
      </StatGrid>

      <div style={{ height: theme.space[6] }} />

      <MatchRateHero
        matched={matched}
        pending={pendingReview}
        unmatched={unmatched}
        total={totalRecords}
      />

      {unmatched > 0 && <UnmatchedSection />}
    </>
  );
}

/* ═══════════════ UTILITY ═══════════════ */

function ClickableStat({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: 'pointer',
        transition: `background ${theme.motion.fast}`,
        height: '100%',
      }}
      onMouseEnter={e => {
        (e.currentTarget.firstElementChild as HTMLElement).style.background = theme.surface.cardHover;
      }}
      onMouseLeave={e => {
        (e.currentTarget.firstElementChild as HTMLElement).style.background = theme.surface.panel;
      }}
    >
      {children}
    </div>
  );
}

function LegendItem({ color, glow, dashed, label, value }: {
  color: string;
  glow?: boolean;
  dashed?: boolean;
  label: string;
  value: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: theme.space[2] }}>
      <span style={{
        width: 12, height: 2, borderRadius: 1,
        background: dashed
          ? `repeating-linear-gradient(90deg, ${color} 0 3px, transparent 3px 6px)`
          : color,
        boxShadow: glow ? `0 0 4px ${theme.brand.cyanGlow}` : 'none',
        flexShrink: 0,
      }} />
      <span style={{ fontSize: theme.fontSize.xs, color: theme.text.secondary }}>{label}</span>
      <span style={{
        fontFamily: theme.font.data,
        fontSize: theme.fontSize.xs,
        color: theme.text.primary,
        fontWeight: theme.fontWeight.medium,
      }}>{value}</span>
    </div>
  );
}

/* ═══════════════ UNMATCHED RECORDS (inline section) ═══════════════ */
/* Only renders in client or demo-view-as-client mode when unmatched > 0.
   Backend client-records endpoint scopes by JWT / demo header, so passing
   an explicit client is optional — we pass it when known for precision. */

interface ClientRecordRow {
  id: string;
  raw_name: string;
  raw_address: string;
  raw_city: string;
  raw_vat: string;
}

function UnmatchedSection({ clientName }: { clientName?: string | null }) {
  const [items, setItems] = useState<ClientRecordRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { modeVersion } = useAdmin();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams({ match_method: 'unmatched', limit: '25' });
    if (clientName) params.set('client', clientName);
    api.get(`/api/client-records?${params.toString()}`, { signal: controller.signal })
      .then(res => setItems(res.data.items ?? []))
      .catch(err => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setItems([]);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [clientName, modeVersion]);

  const thStyle = {
    padding: '10px 14px',
    textAlign: 'left' as const,
    color: theme.text.tertiary,
    fontSize: 10,
    fontWeight: theme.fontWeight.semibold,
    letterSpacing: theme.letterSpacing.wider,
    textTransform: 'uppercase' as const,
  };
  const tdStyle = {
    padding: '10px 14px',
    color: theme.text.primary,
    fontSize: theme.fontSize.sm,
  };

  return (
    <div style={{ marginTop: theme.space[6] }}>
      <div style={{ marginBottom: theme.space[4] }}>
        <div style={{
          width: 40, height: 3,
          background: theme.accent.amber,
          borderRadius: 2,
          marginBottom: theme.space[3],
        }} />
        <h2 style={{
          fontSize: theme.fontSize.lg,
          fontWeight: theme.fontWeight.semibold,
          color: theme.text.primary,
          margin: 0,
          letterSpacing: '-0.01em',
        }}>Unmatched records</h2>
        <p style={{
          fontSize: theme.fontSize.sm,
          color: theme.text.secondary,
          marginTop: 4,
          marginBottom: 0,
        }}>
          These records could not be matched to the Meridian Registry. Contact your administrator to resolve.
        </p>
      </div>

      <Card padding="none">
        {loading ? (
          <div style={{ padding: theme.space[4] }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ marginBottom: 10 }}>
                <Skeleton width="100%" height={16} />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: theme.space[6] }}>
            <EmptyState
              compact
              icon={<Icon.Check size={18} />}
              title="No unmatched records"
              description="Every submission was matched to the registry."
            />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: theme.fontSize.sm,
            }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.borderColor.default}` }}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Address</th>
                  <th style={thStyle}>City</th>
                  <th style={thStyle}>VAT</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr
                    key={r.id}
                    style={{
                      borderBottom: `1px solid ${theme.borderColor.subtle}`,
                      background: i % 2 === 1 ? 'rgba(255,255,255,0.01)' : 'transparent',
                    }}>
                    <td style={tdStyle}>{r.raw_name || '—'}</td>
                    <td style={{ ...tdStyle, color: theme.text.secondary }}>{r.raw_address || '—'}</td>
                    <td style={{ ...tdStyle, color: theme.text.secondary }}>{r.raw_city || '—'}</td>
                    <td style={{ ...tdStyle, fontFamily: theme.font.data, color: theme.text.secondary }}>{r.raw_vat || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
